import { useState } from 'react'

import { useTranslation } from 'react-i18next'

import { BasicTextarea } from '@/Atomic/Atoms/Inputs/BasicTextarea'
import { GlobalLoadingSpinner } from '@/Atomic/Atoms/Spinner'
import { Button } from '@/Atomic/Molecules/Button'
import {
  Drawer,
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
} from '@/Atomic/Templates/Drawer'
import {
  AddressFields,
  extractAddressFromText,
} from '@/Components/Address/addressTextExtract'

interface DataClassAddressTextExtractDrawerProps {
  isOpen: boolean
  close: () => void
  onExtracted: (data: AddressFields) => void
}

export const DataClassAddressTextExtractDrawer: React.FC<
  DataClassAddressTextExtractDrawerProps
> = ({ isOpen, close, onExtracted }) => {
  const { t } = useTranslation()
  const [text, setText] = useState('')
  const [isProcessing, setIsProcessing] = useState(false)

  const onClose = () => {
    setText('')
    close()
  }

  const handleExtract = async () => {
    if (!text.trim()) return
    setIsProcessing(true)
    try {
      const result = await extractAddressFromText(text, t)
      if (result) {
        onExtracted(result.address)
        setText('')
      }
    } finally {
      setIsProcessing(false)
    }
  }

  return (
    <>
      <Drawer isOpen={isOpen} close={onClose} direction="right">
        <DrawerHeader>
          <div className="header item">
            {t('components.address.textExtract.title')}
          </div>
          <div className="header item">
            <Button
              variant="icon"
              icon="cross"
              tooltip={t('tooltip.close')}
              onClick={onClose}
            />
          </div>
        </DrawerHeader>
        <DrawerBody>
          <BasicTextarea
            name="addressText"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={t('components.address.textExtract.placeholder')}
            autoFocus={isOpen}
            style={{ minHeight: '160px', resize: 'vertical' }}
          />
        </DrawerBody>
        <DrawerFooter align="right">
          <Button variant="outlined" onClick={onClose} size="lg">
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleExtract}
            size="lg"
            disabled={!text.trim() || isProcessing}
          >
            {t('components.address.textExtract.extract')}
          </Button>
        </DrawerFooter>
      </Drawer>
      <GlobalLoadingSpinner isLoading={isProcessing} />
    </>
  )
}
